// Messagerie (internal team chat) page — web.
// Faithful reproduction of heric's MessagerieScreen layout: a 380px white
// discussions sidebar (tabs + search + list) and the chat thread, same colors
// and Poppins typography. Wired to the hexagone messagerie store.

import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../config/FirebaseConfig";
import { getCurrentUser } from "../config/getFirebaseToken";
import { useAppContext } from "../config/AppContext";
import useMessagerieStore from "../stores/messagerieStore";
import MessagerieThread from "./MessagerieThread";
import {
  Icon,
  AvatarStack,
  getMember,
  formatListTime,
} from "./messagerieUi";
import {
  useNotificationStatus,
  NotificationsBell,
  NotificationsPrompt,
} from "./NotificationsControl";


function SearchField({ value, onChange }) {
  return (
    <div className="flex flex-row items-center" style={{ background: "#F5F5F5", borderRadius: 10, padding: "8px 12px", gap: 8 }}>
      <Icon name="search" size={15} color="#A1A1A1" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Chercher .."
        className="outline-none border-none flex-1"
        style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000", background: "transparent" }}
      />
    </div>
  );
}

// --- Discussion list item ---
function DiscussionItem({ discussion, members, isSelected, unread, onSelect }) {
  const discMembers = useMemo(
    () => (members || []).filter((m) => discussion.members.includes(m.id)).map((u) => getMember([u], u.id)),
    [members, discussion.members]
  );
  const lm = discussion.last_message;
  return (
    <button
      onClick={() => onSelect(discussion.id)}
      className="flex flex-row items-center w-full text-left"
      style={{ padding: 10, gap: 25, height: 56, borderRadius: 10, background: isSelected ? "#F5F5F5" : "transparent" }}
    >
      <AvatarStack members={discMembers} size={28} maxVisible={3} fixedWidth={55} />
      <div className="flex flex-col flex-1" style={{ minWidth: 0 }}>
        <div className="flex flex-row justify-between items-end">
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, lineHeight: "21px", color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {discussion.name}
          </span>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, lineHeight: "18px", color: "#A1A1A1", flexShrink: 0 }}>
            {formatListTime(lm?.timestamp || discussion.updated_at)}
          </span>
        </div>
        <div className="flex flex-row justify-between items-center" style={{ gap: 5 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, lineHeight: "15px", color: "#A1A1A1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {lm?.text || "Aucun message"}
          </span>
          {unread > 0 && (
            <span className="flex items-center justify-center" style={{ minWidth: 18, height: 18, borderRadius: 9, background: "rgba(45,46,210,1)", padding: "0 5px", fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#FFFFFF", flexShrink: 0 }}>
              {unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// --- New discussion modal ---
function NewDiscussionModal({ onClose, members, currentUserId, onCreate }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const others = (members || []).filter((m) => m.id !== currentUserId).map((u) => getMember([u], u.id));
  const filtered = memberSearch ? others.filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase())) : others;

  const submit = async () => {
    if (!name.trim() || !selected.length) return;
    setBusy(true);
    try { await onCreate({ tab: "generales", name: name.trim(), memberIds: selected }); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <div className="flex flex-col" style={{ width: 420, maxWidth: "90vw", background: "#FFFFFF", borderRadius: 16, overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-row items-center justify-between" style={{ padding: "16px 20px", borderBottom: "1px solid #F0F0F0" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#000000" }}>Nouvelle discussion</span>
          <button onClick={onClose} className="hover:bg-neutral-100 rounded-md flex items-center justify-center" style={{ width: 28, height: 28 }}>
            <Icon name="close" size={12} color="#A1A1A1" />
          </button>
        </div>
        <div className="flex flex-col" style={{ padding: 20, gap: 16 }}>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#525252" }}>Nom</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom de la discussion"
              className="outline-none"
              style={{ fontFamily: "Poppins", fontSize: 14, color: "#000000", background: "#F5F5F5", borderRadius: 10, padding: "10px 12px", border: "none" }}
            />
          </div>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#525252" }}>Membres</span>
            <SearchField value={memberSearch} onChange={setMemberSearch} />
            <div className="flex flex-col" style={{ maxHeight: 220, overflowY: "auto", marginTop: 4 }}>
              {filtered.map((m) => {
                const checked = selected.includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelected((prev) => (checked ? prev.filter((x) => x !== m.id) : [...prev, m.id]))}
                    className="flex flex-row items-center justify-between hover:bg-neutral-50"
                    style={{ padding: "8px 6px", borderRadius: 8 }}
                  >
                    <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000000" }}>{m.name}</span>
                    <span className="flex items-center justify-center" style={{ width: 18, height: 18, borderRadius: 5, border: checked ? "none" : "1.5px solid #D4D4D4", background: checked ? "#6366f1" : "transparent" }}>
                      {checked && <Icon name="check" size={11} color="#FFFFFF" />}
                    </span>
                  </button>
                );
              })}
              {!filtered.length && (
                <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", padding: 8 }}>Aucun membre</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-end" style={{ padding: "12px 20px", gap: 10, borderTop: "1px solid #F0F0F0" }}>
          <button onClick={onClose} className="hover:bg-neutral-100" style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#525252", padding: "9px 16px", borderRadius: 10 }}>Annuler</button>
          <button
            onClick={submit}
            disabled={busy || !name.trim() || !selected.length}
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#FFFFFF", background: !name.trim() || !selected.length ? "#C7C7C7" : "#000000", padding: "9px 18px", borderRadius: 10 }}
          >
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Sidebar ---
function DiscussionsPanel({ members, onNew, notif }) {
  const discussions = useMessagerieStore((s) => s.discussions);
  const unreadCounts = useMessagerieStore((s) => s.unreadCounts);
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const selectDiscussion = useMessagerieStore((s) => s.selectDiscussion);

  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return discussions.filter((d) => !q || (d.name || "").toLowerCase().includes(q));
  }, [discussions, searchQuery]);

  return (
    <div className="flex flex-col flex-shrink-0" style={{ width: 380, height: "100%", padding: "19px 20px 23px", gap: 25, background: "#FFFFFF", borderRight: "1px solid #DDDDDD", overflow: "hidden" }}>
      <div className="flex flex-row items-center justify-between" style={{ padding: "0 8px" }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 16, lineHeight: "24px", color: "#000000" }}>Discussions</span>
        <div className="flex flex-row items-center" style={{ gap: 2 }}>
          {notif && <NotificationsBell {...notif} />}
          <button onClick={onNew} className="hover:bg-neutral-100 rounded-md flex items-center justify-center" style={{ width: 32, height: 32 }}>
            <Icon name="plus" size={14} color="#6366f1" />
          </button>
        </div>
      </div>

      <SearchField value={searchQuery} onChange={setSearchQuery} />

      {notif && <NotificationsPrompt {...notif} />}

      <div className="flex flex-col flex-1" style={{ overflowY: "auto", minHeight: 0 }}>
        {filtered.map((d) => (
          <DiscussionItem
            key={d.id}
            discussion={d}
            members={members}
            isSelected={d.id === selectedDiscussionId}
            unread={unreadCounts[d.id] || 0}
            onSelect={selectDiscussion}
          />
        ))}
        {!filtered.length && (
          <div className="text-center" style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", padding: "24px 0" }}>
            Aucune discussion.
          </div>
        )}
      </div>
    </div>
  );
}

export default function MessagerieScreen({ userData, orgData, navigateWorkspace }) {
  const { appState } = useAppContext();
  const prefix = appState?.dev ? appState.dev_url : appState.prod_url;
  const userId = userData?.id || getCurrentUser()?.uid;
  const orgId = userData?.org_id || orgData?.id;

  const discussions = useMessagerieStore((s) => s.discussions);
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const members = useMessagerieStore((s) => s.members);
  const init = useMessagerieStore((s) => s.init);
  const teardown = useMessagerieStore((s) => s.teardown);
  const setMembers = useMessagerieStore((s) => s.setMembers);
  const selectDiscussion = useMessagerieStore((s) => s.selectDiscussion);
  const createDiscussion = useMessagerieStore((s) => s.createDiscussion);

  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const notif = useNotificationStatus(userId, prefix);

  useEffect(() => {
    if (!prefix || !orgId || !userId) return;
    init({ prefix, orgId, userId });
    return () => teardown();
  }, [prefix, orgId, userId, init, teardown]);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "user"), where("org_id", "==", orgId)));
        setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn("[messagerie] failed to load org members:", e.message);
      }
    })();
  }, [orgId, setMembers]);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);

  return (
    <div className="flex flex-row h-full w-full max-h-full overflow-hidden relative" style={{ minHeight: 0 }}>
      <DiscussionsPanel members={members} onNew={() => setShowNewDiscussion(true)} notif={notif} />
      <MessagerieThread discussion={discussion} userId={userId} orgId={orgId} navigateWorkspace={navigateWorkspace} />
      {showNewDiscussion && (
        <NewDiscussionModal
          onClose={() => setShowNewDiscussion(false)}
          members={members}
          currentUserId={userId}
          onCreate={async (payload) => {
            const created = await createDiscussion(payload);
            setShowNewDiscussion(false);
            if (created?.id) selectDiscussion(created.id);
          }}
        />
      )}
    </div>
  );
}
